import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ZONES, zoneOfCity, citySlug } from "@/lib/zones";
import { useSeo } from "@/hooks/useSeo";
import DepartmentMap from "@/components/DepartmentMap";
import DepartmentWideBanner from "@/components/DepartmentWideBanner";
import { MapPin, Compass, ArrowRight } from "lucide-react";

export default function Cities() {
  useSeo({
    title: "Villes et communes d'Eure-et-Loir — commerces & bons plans locaux | PROXIreduc-28",
    description: "Explorez les commerces, services et bons plans par ville en Eure-et-Loir : Chartres, Dreux, Châteaudun, Nogent-le-Rotrou, Lucé et toutes les communes du département 28.",
    canonicalPath: "/villes",
    image: "/og-image.jpg",
  });
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [territoryCfg, setTerritoryCfg] = useState(null);

  useEffect(() => {
    document.title = "Bons plans par ville et par pôle en Eure-et-Loir | PROXIreduc-28";
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    md.setAttribute("content", "Explorez les bons plans des 9 pôles d'Eure-et-Loir, commune par commune : Chartres, Dreux, Châteaudun, Nogent-le-Rotrou, Bonneval, Courville-sur-Eure, Illiers-Combray, Brou, Épernon.");
  }, []);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/offers?limit=500").then((r) => setOffers(r.data));
    api.get("/businesses-geo").then((r) => setBusinesses(r.data)).catch(() => setBusinesses([]));
    api.get("/public/territory-config").then((r) => setTerritoryCfg(r.data || null)).catch(() => {});
  }, []);

  const { countsByZone, deptWideCount } = useMemo(() => {
    const map = {}; let dept = 0;
    offers.forEach((o) => {
      if (o.coverage === "department") dept += 1;
      const z = zoneOfCity(o.city);
      if (z) map[z.id] = (map[z.id] || 0) + 1;
    });
    return { countsByZone: map, deptWideCount: dept };
  }, [offers]);

  // Zones réparties selon le pôle prioritaire (dynamique, défaut: chartres)
  const priorityId = territoryCfg?.priority_id || "chartres";
  const priorityZone = ZONES.find((z) => z.id === priorityId) || ZONES[0];
  const otherZones = ZONES.filter((z) => z.id !== priorityId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Carte des pôles</div>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold">9 pôles en Eure-et-Loir</h1>
        <p className="mt-2 text-slate-500">Cliquez sur un pôle pour explorer ses bons plans. Chaque zone couvre environ 15 km à la ronde.</p>
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-3xl p-4 md:p-8" data-testid="dept-map-wrapper">
        <DepartmentMap counts={countsByZone} businesses={businesses} />
      </div>

      <div className="mt-8">
        <DepartmentWideBanner count={deptWideCount} />
      </div>

      {/* Groupe 1 : pôle prioritaire + son agglomération */}
      <section className="mt-10" data-testid="cities-priority-section">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#E05638] text-white grid place-items-center"><MapPin size={18}/></div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#E05638]">Cœur du réseau</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">{priorityZone.center} & son agglomération</h2>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to={`/pole/${priorityZone.id}`}
                data-testid={`pole-card-${priorityZone.id}`}
                className="card-hover bg-gradient-to-br from-[#E05638] to-orange-500 text-white rounded-2xl p-5 flex items-center gap-3 shadow-lg shadow-orange-200/40">
            <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center"><Compass size={20}/></div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-lg">{priorityZone.name}</div>
              <div className="text-xs text-white/90">{countsByZone[priorityZone.id] || 0} offres · {priorityZone.cities.length} communes</div>
            </div>
            <ArrowRight size={18}/>
          </Link>
        </div>
        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Communes couvertes</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {priorityZone.cities.map((c) => (
              <Link key={c} to={`/bons-plans/ville/${citySlug(c)}`} data-testid={`city-link-${citySlug(c)}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-[#E05638] hover:text-[#E05638] transition-colors">
                <MapPin size={12}/> {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Groupe 2 : autres territoires */}
      <section className="mt-12" data-testid="cities-secondary-section">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center"><MapPin size={18}/></div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-blue-700">Partout en Eure-et-Loir</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Les autres territoires du réseau</h2>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {otherZones.map((z) => (
            <Link key={z.id} to={`/pole/${z.id}`}
                  data-testid={`pole-card-${z.id}`}
                  className="card-hover bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center"><MapPin size={20}/></div>
              <div className="flex-1">
                <div className="font-heading font-bold text-slate-900">{z.name}</div>
                <div className="text-xs text-slate-500">{countsByZone[z.id] || 0} offres · {z.cities.length} communes</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Maillage interne SEO : chaque commune de ce groupe */}
        <div className="mt-8 space-y-5" data-testid="cities-seo-links">
          <h3 className="font-heading text-xl font-extrabold text-slate-900">Bons plans commune par commune</h3>
          {otherZones.map((z) => (
            <div key={z.id}>
              <div className="text-[11px] font-bold uppercase tracking-widest text-blue-700">
                <Link to={`/pole/${z.id}`} className="hover:underline">{z.name}</Link>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {z.cities.map((c) => (
                  <Link key={c} to={`/bons-plans/ville/${citySlug(c)}`} data-testid={`city-link-${citySlug(c)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-blue-500 hover:text-blue-700 transition-colors">
                    <MapPin size={12}/> {c}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
